import { useOrganizationTest } from "~/common/useOrganizationTest"
import { useQuery } from "@apollo/client"
import { useParams, useNavigate } from "react-router-dom"
import invariant from "tiny-invariant"
import { LoadingIndicatorCentered } from "~/components/LoadingIndicator"
import { EmptyData } from "~/components/EmptyData"
import { Error } from "~/ui/Error"
import { CandidateTestLayout } from "~/layouts/CandidateTestLayout"
import {
  organizationQuestionResponsePath,
  organizationTestResponsesPath,
  organizationCandidatesPath,
} from "~/common/paths"
import { useOrganizationId } from "~/common/useCurrentOrganization"
import { TestQuestionResponsesList } from "~/tests/TestQuestionResponsesList"
import { Navigation } from "~/organizations/Navigation"
import {
  CANDIDATE_TEST_RESPONSES_QUERY_DOCUMENT,
  MARK_CANDIDATE_TEST_AS_SEEN_RESPONSE_MUTATION_DOCUMENT,
} from "~/tests/candidateTestQueries"
import { ShareButton } from "~/tests/ShareUI"
import { EmailCandidateDialog } from "~/organizations/EmailCandidateDialog"
import { CandidateTestNoteDialog } from "~/organizations/CandidateTestNoteDialog"
import { CandidateTestNotesList } from "~/organizations/CandidateTestNotesList"
import { ToggleArchivedButton } from "~/tests/ToggleArchivedAtButton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/shadcn/ui/tabs"
import { useState, useEffect, useMemo } from "react"
import { useSafeMutation } from "~/common/useSafeMutation"
import { ReviewStatusButton } from "~/tests/ReviewStatusButton"
import { Badge } from "~/shadcn/ui/badge"
import { AlertTriangle } from "lucide-react"

export const OrganizationTestResponseScreen = () => {
  const { test } = useOrganizationTest()
  const { candidateTestId } = useParams()
  invariant(candidateTestId)
  const navigate = useNavigate()

  const { loading, data, error } = useQuery(
    CANDIDATE_TEST_RESPONSES_QUERY_DOCUMENT,
    {
      variables: { candidateTestId },
    }
  )

  const [selectedTab, setSelectedTab] = useState<string>("summary")
  const [markSeenAt] = useSafeMutation(
    MARK_CANDIDATE_TEST_AS_SEEN_RESPONSE_MUTATION_DOCUMENT
  )

  useEffect(() => {
    if (data?.candidateTest && !data.candidateTest.seenAt) {
      markSeenAt({ variables: { input: { candidateTestId } } }).catch(() => {
        console.warn("error occured marking candidate test as seen.")
      })
    }
  }, [data, candidateTestId, markSeenAt])

  const organizationId = useOrganizationId()
  const testId = test?.id || ""

  const onArchived = () => {
    navigate(organizationCandidatesPath({ organizationId }))
  }

  const calculateTotalNavigatedAwayCount = useMemo(() => {
    let total = 0

    data?.candidateTestQuestionResponses.nodes.forEach((response) => {
      total += response.navigatedAwayCount
    })
    return total
  }, [data?.candidateTestQuestionResponses.nodes])

  return (
    <CandidateTestLayout
      test={test}
      candidateTest={data?.candidateTest}
      backLabel="Responses"
      backPath={organizationTestResponsesPath({ organizationId, testId })}
      navigation={
        <Navigation organizationId={organizationId} testId={testId} />
      }
      rightProfile={
        <div className="flex items-center gap-2">
          <EmailCandidateDialog candidateTestId={candidateTestId} />
          <CandidateTestNoteDialog
            candidateTestId={candidateTestId}
            onTabChange={() => setSelectedTab("discussion")}
          />
          {data && (
            <>
              <ToggleArchivedButton
                candidateTest={data?.candidateTest}
                showIcon
                onChange={onArchived}
              />
              <ShareButton candidateTestId={data.candidateTest.id} />
            </>
          )}
        </div>
      }
      overProfile={
        <>
          {calculateTotalNavigatedAwayCount > 0 && (
            <div className="flex items-center px-3 py-2 rounded text-sm border border-yellow-dark bg-yellow-light text-gray-2d2d2d">
              <AlertTriangle
                size={16}
                className="inline mr-3 text-yellow-dark"
              />
              <span className="text-sm">
                During the test this candidate navigated away from the test
                {" - "}
                <span className="font-semibold">
                  {calculateTotalNavigatedAwayCount} times
                </span>
              </span>
            </div>
          )}
        </>
      }
      rightModule={
        data && (
          <div className="flex items-center float-right">
            <div className="p-6 text-sm text-gray-999">Review Status</div>
            <ReviewStatusButton
              organizationId={organizationId}
              candidateTest={data?.candidateTest}
            />
          </div>
        )
      }
    >
      {error ? <Error message="Error loading responses." /> : null}
      {loading ? (
        <LoadingIndicatorCentered />
      ) : data && data.candidateTestQuestionResponses.nodes.length > 0 ? (
        <>
          <Tabs
            value={selectedTab}
            onValueChange={setSelectedTab}
            className="w-full"
          >
            <TabsList className="w-full items-end justify-start rounded-none border-b bg-transparent p-0">
              <TabsTrigger
                value="summary"
                className="relative -bottom-[1px] h-9 rounded-none border-b border-b-transparent bg-transparent px-0 mr-8 pb-4 pt-2 font-normal text-base text-black shadow-none transition-none data-[state=active]:border-b-black data-[state=active]:text-foreground data-[state=active]:shadow-none"
              >
                Test Summary
              </TabsTrigger>
              <TabsTrigger
                value="discussion"
                className="relative -bottom-[1px] h-9 rounded-none border-b border-b-transparent bg-transparent px-0 mr-8 pb-4 pt-2 font-normal text-base text-black shadow-none transition-none data-[state=active]:border-b-black data-[state=active]:text-foreground data-[state=active]:shadow-none"
              >
                Discussion
                {data.candidateTest.notes.length > 0 && (
                  <div className="ml-2 text-white text-xs w-6 h-6 rounded-full bg-primary flex items-center justify-center text-[8px]">
                    {data.candidateTest.notes.length}
                  </div>
                )}
              </TabsTrigger>
            </TabsList>
            <TabsContent
              value="summary"
              className="pt-10 w-full flex flex-col gap-10"
            >
              {(data.candidateTest.aiBio || data.candidateTest.aiSummary) && (
                <>
                  <div className="flex flex-col gap-4 p-10 border border-gray-E6E6E3 rounded-lg max-h-[300px] overflow-y-auto">
                    {data.candidateTest.aiBio && (
                      <div className="flex flex-col gap-2">
                        <div className="text-gray-999 text-lg font-medium">
                          Candidate Bio
                          <Badge className="ml-2" variant="default">
                            Beta
                          </Badge>
                        </div>
                        <div className="text-gray-333 text-base leading-7">
                          {data.candidateTest.aiBio}
                        </div>
                      </div>
                    )}
                    {data.candidateTest.aiSummary && (
                      <div className="flex flex-col gap-2">
                        <div className="text-gray-999 text-lg font-medium">
                          Test Summary
                          <Badge className="ml-2" variant="default">
                            Beta
                          </Badge>
                        </div>
                        <div className="text-gray-333 text-base leading-7">
                          {data.candidateTest.aiSummary}
                        </div>
                      </div>
                    )}
                  </div>
                  <h3 className="text-2xl">Candidate Responses</h3>
                </>
              )}
              <TestQuestionResponsesList
                data={data.candidateTestQuestionResponses.nodes}
                viewPath={(response) =>
                  organizationQuestionResponsePath({
                    organizationId,
                    testId,
                    questionResponseId: response.id,
                  })
                }
                onlyAnswered
              />
            </TabsContent>
            <TabsContent
              value="discussion"
              className="pt-4 w-full flex flex-column p-2 border border-gray-E6E6E3 rounded"
            >
              <CandidateTestNotesList notes={data?.candidateTest.notes} />
            </TabsContent>
          </Tabs>
        </>
      ) : (
        <EmptyData>No responses yet.</EmptyData>
      )}
    </CandidateTestLayout>
  )
}
